import { useMemo, useState } from "react";
import { CheckCircle } from "lucide-react";
import WhatsAppCta from "@/components/base/WhatsAppCta";
import {
  FIRMA_ADDON_ROWS,
  FIRMA_CLOSING,
  FIRMA_CONTABLE,
  FIRMA_CTA_PRIMARY,
  FIRMA_FEATURES,
  FIRMA_FOOTNOTE,
  FIRMA_HOOK,
  FIRMA_RNC_ROWS,
  FIRMA_WHATSAPP_URL,
  pymeName,
  pymePrice,
} from "@/lib/plans";
import { priceFirmaContable } from "@/lib/pricing";

function formatUsd(value: number, digits = 0) {
  return value.toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function formatSavings(ratio: number) {
  const pct = Math.round(ratio * 100);
  if (pct > 0) return `−${pct}%`;
  if (pct < 0) return `+${Math.abs(pct)}%`;
  return "0%";
}

function clampCount(value: number) {
  return Math.max(0, Math.min(80, Number.isFinite(value) ? Math.floor(value) : 0));
}

type FirmaContablePricingProps = {
  showIntro?: boolean;
};

export default function FirmaContablePricing({ showIntro = false }: FirmaContablePricingProps) {
  const [basico, setBasico] = useState(5);
  const [plus, setPlus] = useState(0);
  const [empresarial, setEmpresarial] = useState(0);
  const quote = useMemo(
    () => priceFirmaContable({ basico, plus, empresarial }),
    [basico, plus, empresarial],
  );

  const setupAddon = FIRMA_CONTABLE.addons.find((item) => item.id === "rnc_setup");

  return (
    <div className="space-y-10">
      {showIntro && (
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-flex items-center px-3 py-1 rounded-full border border-slate-200 text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">
            {FIRMA_CONTABLE.badge}
          </span>
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">{FIRMA_CONTABLE.name}</h3>
          <p className="text-slate-500 text-sm md:text-base">{FIRMA_CONTABLE.tagline}</p>
        </div>
      )}

      <article className="relative bg-white rounded-2xl p-8 border border-blue-800 shadow-lg shadow-blue-900/10 max-w-xl mx-auto">
        <div className="absolute -top-3 left-6">
          <span className="bg-blue-50 text-blue-800 text-xs font-bold px-4 py-1 rounded-full border border-blue-100">
            {FIRMA_CONTABLE.badge}
          </span>
        </div>

        <div className="mb-6 mt-2">
          <h3 className="text-lg font-bold text-slate-900 mb-1">{FIRMA_CONTABLE.name}</h3>
          <p className="text-sm text-slate-500 mb-4">{FIRMA_CONTABLE.tagline}</p>
          <div className="flex items-end gap-1 flex-wrap">
            <span className="text-sm text-slate-500 mb-1">{FIRMA_CONTABLE.currency}$</span>
            <span className="font-black text-slate-900 text-4xl">{FIRMA_CONTABLE.base_price}</span>
            <span className="text-sm text-slate-400 mb-1">/mes</span>
            <span className="text-sm text-slate-500 mb-1 italic ml-1">
              — {FIRMA_CONTABLE.included_rnc} clientes incluidos
            </span>
          </div>
          <p className="text-sm font-medium text-emerald-600 mt-3">{FIRMA_HOOK}</p>
        </div>

        <ul className="space-y-3 mb-8">
          {FIRMA_FEATURES.map((f) => (
            <li key={f} className="flex items-start gap-2.5 text-sm text-slate-600">
              <CheckCircle size={15} className="text-emerald-500 flex-shrink-0 mt-0.5" />
              {f}
            </li>
          ))}
        </ul>

        <WhatsAppCta href={FIRMA_WHATSAPP_URL} className="w-full py-2.5">
          {FIRMA_CTA_PRIMARY}
        </WhatsAppCta>

        <p className="text-xs text-slate-400 leading-relaxed mt-6">{FIRMA_FOOTNOTE}</p>
      </article>

      <div>
        <h4 className="text-sm font-bold text-slate-900 mb-3">Tipos de cliente</h4>
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-5 py-3 font-semibold">Tipo de cliente</th>
                <th className="px-5 py-3 font-semibold">Precio / mes</th>
                <th className="px-5 py-3 font-semibold">e-CF</th>
                <th className="px-5 py-3 font-semibold">Usuarios</th>
                <th className="px-5 py-3 font-semibold">Contratado por separado</th>
              </tr>
            </thead>
            <tbody>
              {FIRMA_CONTABLE.rnc_types.map((type) => (
                <tr key={type.id} className="border-t border-slate-100">
                  <td className="px-5 py-3 font-medium text-slate-800">{type.label}</td>
                  <td className="px-5 py-3 text-slate-600">
                    {type.tiers
                      ? type.tiers.map((t) => `$${t.price}`).join(" / ") + " según escalera"
                      : `$${type.price}`}
                  </td>
                  <td className="px-5 py-3 text-slate-600">{type.ecf.toLocaleString("en-US")}</td>
                  <td className="px-5 py-3 text-slate-600">{type.users}</td>
                  <td className="px-5 py-3 text-slate-600">
                    {pymeName(type.equivalentPlanCode)} ${pymePrice(type.equivalentPlanCode)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h4 className="text-sm font-bold text-slate-900 mb-3">Escalera del RNC Básico</h4>
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-5 py-3 font-semibold">Clientes</th>
                <th className="px-5 py-3 font-semibold">Precio por cliente / mes</th>
              </tr>
            </thead>
            <tbody>
              {FIRMA_RNC_ROWS.map((row) => (
                <tr key={row.clients} className="border-t border-slate-100">
                  <td className="px-5 py-3 font-medium text-slate-800">{row.clients}</td>
                  <td className="px-5 py-3 text-slate-600">{row.price}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-500 mt-3">{FIRMA_CLOSING}</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 space-y-4">
        <h4 className="text-lg font-bold text-slate-900">¿Y si un cliente factura mucho más que los demás?</h4>
        <p className="text-sm text-slate-600 leading-relaxed">
          Cada cliente entra según su volumen: Básico hasta 150 comprobantes, Plus hasta 500, Empresarial hasta 2,000.
          No pagas precio de cliente grande por tu cartera de clientes chicos, y ninguno de tus clientes se queda sin
          emitir por consumir la cuota de otro.
        </p>
        <p className="text-sm text-slate-600 leading-relaxed">
          Si un cliente crece por encima de su tipo dos meses seguidos, te avisamos 15 días antes de subirlo. Sin
          cargos retroactivos y sin sorpresas en la factura.
        </p>
        <h4 className="text-lg font-bold text-slate-900 pt-2">¿Y si toda tu cartera sube en temporada de cierre?</h4>
        <p className="text-sm text-slate-600 leading-relaxed">
          Compras capacidad por adelantado desde $0.075 por comprobante y la usas cuando la necesitas: los paquetes
          tienen vigencia de 12 meses y el saldo no se pierde a fin de mes.
        </p>
        <div className="overflow-x-auto rounded-2xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-5 py-3 font-semibold">Paquete</th>
                <th className="px-5 py-3 font-semibold">Precio</th>
                <th className="px-5 py-3 font-semibold">Por e-CF</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-slate-100">
                <td className="px-5 py-3 text-slate-800">Excedente reactivo</td>
                <td className="px-5 py-3 text-slate-600">
                  ${FIRMA_CONTABLE.overage.price} / {FIRMA_CONTABLE.overage.block}
                </td>
                <td className="px-5 py-3 text-slate-600">
                  ${(FIRMA_CONTABLE.overage.price / FIRMA_CONTABLE.overage.block).toFixed(3)}
                </td>
              </tr>
              {FIRMA_CONTABLE.pool_packs.map((pack) => (
                <tr key={pack.ecf} className="border-t border-slate-100">
                  <td className="px-5 py-3 text-slate-800">Pack {pack.ecf.toLocaleString("en-US")}</td>
                  <td className="px-5 py-3 text-slate-600">${pack.price}</td>
                  <td className="px-5 py-3 text-slate-600">${pack.unit.toFixed(3)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h4 className="text-sm font-bold text-slate-900 mb-3">Add-ons</h4>
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-5 py-3 font-semibold">Add-on</th>
                <th className="px-5 py-3 font-semibold">Precio</th>
              </tr>
            </thead>
            <tbody>
              {FIRMA_ADDON_ROWS.map((row) => (
                <tr key={row.label} className="border-t border-slate-100">
                  <td className="px-5 py-3 text-slate-800">{row.label}</td>
                  <td className="px-5 py-3 font-medium text-slate-700 whitespace-nowrap">{row.price}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div
        id="calcular-plan"
        className="bg-slate-900 text-white rounded-2xl p-6 md:p-8 border border-slate-800 shadow-xl scroll-mt-24"
      >
        <h3 className="text-lg font-bold mb-2">Calcula tu plan</h3>
        <p className="text-slate-400 text-xs mb-6">
          Elige cuántos clientes de cada tipo. El tramo de RNC Básico se aplica cliente por cliente, no de golpe.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {(
            [
              { id: "basico", label: "RNC Básico", value: basico, set: setBasico },
              { id: "plus", label: "RNC Plus", value: plus, set: setPlus },
              { id: "empresarial", label: "RNC Empresarial", value: empresarial, set: setEmpresarial },
            ] as const
          ).map((field) => (
            <label key={field.id} className="block">
              <span className="text-xs font-semibold text-slate-300">{field.label}</span>
              <input
                type="number"
                min={0}
                max={80}
                value={field.value}
                onChange={(e) => field.set(clampCount(Number(e.target.value)))}
                className="mt-2 w-full bg-slate-800 border border-slate-700 text-white text-center font-bold px-3 py-2 rounded text-sm focus:outline-none focus:border-blue-500"
              />
            </label>
          ))}
        </div>

        <dl className="space-y-3 text-sm">
          <div className="flex justify-between border-b border-slate-700 pb-3">
            <dt className="text-slate-400">Mensual</dt>
            <dd className="font-bold text-white">USD$ {formatUsd(quote.monthly)}</dd>
          </div>
          <div className="flex justify-between border-b border-slate-700 pb-3">
            <dt className="text-slate-400">e-CF incluidos</dt>
            <dd className="font-bold text-white">{quote.ecfIncluded.toLocaleString("en-US")}</dd>
          </div>
          <div className="flex justify-between border-b border-slate-700 pb-3">
            <dt className="text-slate-400">Por cliente</dt>
            <dd className="font-bold text-white">USD$ {formatUsd(quote.perClient, 2)}</dd>
          </div>
          <div className="flex justify-between border-b border-slate-700 pb-3">
            <dt className="text-slate-400">Setup año 1</dt>
            <dd className="font-bold text-white">USD$ {formatUsd(quote.setup)}</dd>
          </div>
          <div className="flex justify-between items-end">
            <dt className="text-slate-400">vs. planes individuales</dt>
            <dd className={`font-black text-2xl ${quote.savingsRatio > 0 ? "text-emerald-400" : "text-amber-300"}`}>
              {formatSavings(quote.savingsRatio)}
            </dd>
          </div>
        </dl>

        <p className="mt-6 text-[10px] text-slate-400">
          Comparado contra {pymeName("starter")} ${pymePrice("starter")}, {pymeName("growth")} $
          {pymePrice("growth")} y {pymeName("pro")} ${pymePrice("pro")}.{" "}
          {setupAddon
            ? `Alta: ${setupAddon.price} × ${quote.totalClients} clientes. `
            : null}
          Excedente reactivo: USD${FIRMA_CONTABLE.overage.price} por cada {FIRMA_CONTABLE.overage.block} e-CF.
        </p>
      </div>
    </div>
  );
}


